import { type ProspectTaskDto, type TaskPriority } from "@crowdcoursing/api/openapi";
import { type Theme } from "@mui/material/styles";
import { DateTime } from "luxon";

import { getTimezoneLabel, US_TIMEZONES } from "~constants/timezones";

import { type CarouselTaskItem } from "../TaskDashboardTemplate/taskUtils";

export type CalendarView = "day" | "week" | "month";

/** Pixel height per hour row in day/week views (full 24h day; scroll as needed). */
export const HOUR_ROW_PX = 160;
/** Default block height / time-range label length on the calendar (API has no end time). */
export const DEFAULT_TASK_DURATION_MINUTES = 10;
/** Week view columns: Sunday → Saturday (local). */
export const CALENDAR_WEEK_DAY_COUNT = 7;

export function mergeSalesTasksForCalendar(
  myTasks: ProspectTaskDto[],
  tasksDueSoon: ProspectTaskDto[],
): ProspectTaskDto[] {
  const map = new Map<string, ProspectTaskDto>();
  for (const t of [...myTasks, ...tasksDueSoon]) {
    if (t.id) {
      map.set(t.id, t);
    }
  }
  return [...map.values()];
}

/** Local wall time for the task due instant (invalid ISO → null). */
export function taskDueDateTime(task: CarouselTaskItem): DateTime | null {
  if (!task.dueAt) {
    return null;
  }
  const dt = DateTime.fromISO(task.dueAt, { setZone: true });
  if (!dt.isValid) {
    return null;
  }
  return dt.toLocal();
}

/** Calendar placement time: normalized display instant when present, else API due. */
export function getCalendarDueForDisplay(
  task: CarouselTaskItem,
  normalizedDueByTaskId?: ReadonlyMap<string, DateTime> | null,
): DateTime | null {
  const id = task.id;
  if (id && normalizedDueByTaskId?.has(id)) {
    return normalizedDueByTaskId.get(id) ?? null;
  }
  return taskDueDateTime(task);
}

/**
 * Start of the local calendar week (Sunday 00:00). Luxon: Mon=1 … Sun=7; `weekday % 7` is days to subtract to reach
 * Sunday.
 */
export function getCalendarWeekStartSunday(selected: DateTime): DateTime {
  const d = selected.startOf("day");
  return d.minus({ days: d.weekday % 7 });
}

/** Sunday through Saturday of the week that contains `selected` (local dates). */
export function getCalendarWeekDaysSunSat(selected: DateTime): DateTime[] {
  const sun = getCalendarWeekStartSunday(selected);
  return Array.from({ length: CALENDAR_WEEK_DAY_COUNT }, (_, i) => sun.plus({ days: i }));
}

export function hoursInDay(): number[] {
  return Array.from({ length: 24 }, (_, i) => i);
}

export function formatHourLabel(hour: number): string {
  return DateTime.fromObject({ hour, minute: 0 }).toFormat("h a");
}

export interface TimedPlacement {
  task: CarouselTaskItem;
  startMin: number;
  endMin: number;
  col: number;
  colSpan: number;
}

function overlaps(a: { startMin: number; endMin: number }, b: { startMin: number; endMin: number }): boolean {
  return a.startMin < b.endMin && a.endMin > b.startMin;
}

/**
 * Greedy column assignment for overlapping intervals; each item gets `colSpan` =
 * number of columns needed for its overlap cluster.
 */
export function assignTimedPlacements(
  items: { task: CarouselTaskItem; startMin: number; endMin: number }[],
): TimedPlacement[] {
  if (items.length === 0) {
    return [];
  }

  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const colEndTimes: number[] = [];
  const withCol: { task: CarouselTaskItem; startMin: number; endMin: number; col: number }[] = [];

  for (const it of sorted) {
    let col = 0;
    while (col < colEndTimes.length && colEndTimes[col] > it.startMin) {
      col++;
    }
    colEndTimes[col] = Math.max(colEndTimes[col] ?? 0, it.endMin);
    withCol.push({ ...it, col });
  }
  return withCol.map((it) => {
    const overlapping = withCol.filter((o) => overlaps(o, it));
    const maxCol = Math.max(...overlapping.map((o) => o.col));
    const colSpan = maxCol + 1;
    return { ...it, colSpan };
  });
}

export function timedTasksForDay(
  tasks: CarouselTaskItem[],
  day: DateTime,
  durationMinutes = DEFAULT_TASK_DURATION_MINUTES,
  normalizedDueByTaskId?: ReadonlyMap<string, DateTime> | null,
): TimedPlacement[] {
  const dayStart = day.startOf("day");
  const dayEnd = day.plus({ days: 1 }).startOf("day");

  const raw = tasks
    .map((task) => {
      const due = getCalendarDueForDisplay(task, normalizedDueByTaskId);
      if (!due) {
        return null;
      }
      if (due < dayStart || due >= dayEnd) {
        return null;
      }
      const startMin = due.hour * 60 + due.minute + due.second / 60;
      const endMin = Math.min(startMin + durationMinutes, 24 * 60);
      return { endMin, startMin, task };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  return assignTimedPlacements(raw);
}

const UNDATED_KEY = "__undated__";

export function tasksByLocalDateKey(
  tasks: CarouselTaskItem[],
  normalizedDueByTaskId?: ReadonlyMap<string, DateTime> | null,
): Map<string, CarouselTaskItem[]> {
  const m = new Map<string, CarouselTaskItem[]>();
  for (const t of tasks) {
    const due = getCalendarDueForDisplay(t, normalizedDueByTaskId);
    const key = due?.toISODate() ?? UNDATED_KEY;
    const list = m.get(key) ?? [];
    list.push(t);
    m.set(key, list);
  }
  for (const list of m.values()) {
    list.sort((a, b) => {
      const da = getCalendarDueForDisplay(a, normalizedDueByTaskId);
      const db = getCalendarDueForDisplay(b, normalizedDueByTaskId);
      if (!da && !db) {
        return 0;
      }
      if (!da) {
        return 1;
      }
      if (!db) {
        return -1;
      }
      return da.toMillis() - db.toMillis();
    });
  }
  return m;
}

export function getUndatedTasks(tasks: CarouselTaskItem[]): CarouselTaskItem[] {
  return tasksByLocalDateKey(tasks).get(UNDATED_KEY) ?? [];
}

/** Six-week grid cells covering `monthAnchor`'s month (rows start Sunday, same as week view). */
export function getMonthGridCells(monthAnchor: DateTime): DateTime[] {
  const first = monthAnchor.startOf("month");
  const gridStart = getCalendarWeekStartSunday(first);
  return Array.from({ length: 42 }, (_, i) => gridStart.plus({ days: i }));
}

/** Distinct colors for prospect time zones on the task calendar (and fallback hashing). */
export const CALENDAR_TIMEZONE_PALETTE = [
  "#1565c0",
  "#2e7d32",
  "#6a1b9a",
  "#c62828",
  "#ef6c00",
  "#00838f",
  "#5d4037",
  "#ad1457",
  "#f57f17",
  "#3949ab",
] as const;

function hashStringToPaletteIndex(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) % 997;
  }
  return h % CALENDAR_TIMEZONE_PALETTE.length;
}

/** Stable color for an IANA time zone (known US zones get fixed slots; others hash). */
export function colorForProspectTimeZone(iana: string): string {
  const trimmed = iana.trim();
  if (!trimmed) {
    return CALENDAR_TIMEZONE_PALETTE[0];
  }
  const knownIdx = US_TIMEZONES.findIndex((z) => z.value === trimmed);
  if (knownIdx >= 0) {
    return CALENDAR_TIMEZONE_PALETTE[knownIdx % CALENDAR_TIMEZONE_PALETTE.length];
  }
  return CALENDAR_TIMEZONE_PALETTE[hashStringToPaletteIndex(trimmed)];
}

/** Bar / chip color: prospect time zone when present, else hash by task id (same as previous per-task accents). */
export function calendarAccentColorForTask(task: CarouselTaskItem): string {
  const tz = task.prospectTimeZone?.trim();
  if (tz) {
    return colorForProspectTimeZone(tz);
  }
  if (task.id) {
    return CALENDAR_TIMEZONE_PALETTE[hashStringToPaletteIndex(task.id)];
  }
  return CALENDAR_TIMEZONE_PALETTE[0];
}

/** Same palette as {@link TaskForm} / AddTaskModal priority select. */
export function calendarPriorityFlagColor(priority: TaskPriority | null | undefined, theme: Theme): string {
  switch (priority) {
    case 1:
      return "#d32f2f";
    case 2:
      return "#f57c00";
    case 3:
      return theme.palette.warning.main;
    case 4:
      return theme.palette.primary.main;
    default:
      return theme.palette.action.disabled;
  }
}

export function calendarPriorityAriaLabel(priority: TaskPriority): string {
  switch (priority) {
    case 1:
      return "Urgent priority";
    case 2:
      return "High priority";
    case 3:
      return "Medium priority";
    case 4:
      return "Low priority";
    default:
      return "Priority";
  }
}

/** Only urgent and high show a flag on the calendar (medium/low are omitted). */
export function isUrgentOrHighCalendarPriority(priority: unknown): priority is 1 | 2 {
  return priority === 1 || priority === 2;
}

export interface TimezoneLegendItem {
  color: string;
  iana: string;
  label: string;
}

/** Unique prospect time zones among tasks, sorted by display label (for legend). */
export function buildTimezoneLegendItems(tasks: CarouselTaskItem[]): TimezoneLegendItem[] {
  const seen = new Map<string, true>();
  for (const t of tasks) {
    const tz = t.prospectTimeZone?.trim();
    if (tz) {
      seen.set(tz, true);
    }
  }
  return [...seen.keys()]
    .map((iana) => ({
      color: colorForProspectTimeZone(iana),
      iana,
      label: getTimezoneLabel(iana),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
